import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import type { Vec3 } from "geom/vec3";
import { vec3Add, vec3Cross, vec3Dot, vec3LenSquared, vec3Normalize, vec3Scale } from "geom/vec3";

// Intersect two planes. Finds the line where they intersect.
export function intersectPlanes(plane1: PlaneBSP, plane2: PlaneBSP) {
  const { nX: n1x, nY: n1y, nZ: n1z, dist: d1 } = plane1;
  const { nX: n2x, nY: n2y, nZ: n2z, dist: d2 } = plane2;

  // The direction of the line.
  let [dirX, dirY, dirZ] = vec3Cross(n1x, n1y, n1z, n2x, n2y, n2z);
  // Return null if the two planes are parallel.
  if (vec3LenSquared(dirX, dirY, dirZ) < DIST_EPSILON) {
    return null;
  }
  [dirX, dirY, dirZ] = vec3Normalize(dirX, dirY, dirZ);

  const s = vec3Dot(n1x, n1y, n1z, n2x, n2y, n2z);
  const c1 = (d1 - d2 * s) / (1 - s * s);
  const c2 = (d2 - d1 * s) / (1 - s * s);
  const [x1, y1, z1] = vec3Scale(n1x, n1y, n1z, c1);
  const [x2, y2, z2] = vec3Scale(n2x, n2y, n2z, c2);

  const [px, py, pz] = vec3Add(x1, y1, z1, x2, y2, z2);
  return { dir: [dirX, dirY, dirZ], pos: [px, py, pz] };
}

// Intersect a plane and a line. (ax, ay, az) is a point on the line. (dx, dy, dz) is its direction.
export function intersectLine(
  plane: PlaneBSP,
  ax: number,
  ay: number,
  az: number,
  dx: number,
  dy: number,
  dz: number,
): Vec3 {
  const { nX, nY, nZ } = plane;
  const d0 = vec3Dot(nX, nY, nZ, ax, ay, az);
  const u = vec3Dot(nX, nY, nZ, dx, dy, dz);
  // Return NaNs if the line is parallel to the plane.
  if (u === 0) {
    return [NaN, NaN, NaN];
  }
  const s = (plane.dist - d0) / u;
  const [sx, sy, sz] = vec3Scale(dx, dy, dz, s);
  return vec3Add(ax, ay, az, sx, sy, sz);
}

// Returns true if two planes are coplanar.
export function isCoplanar(planeA: PlaneBSP, planeB: PlaneBSP, tolerance: number = DIST_EPSILON) {
  const { nX: ax, nY: ay, nZ: az } = planeA;
  const { nX: bx, nY: by, nZ: bz } = planeB;
  const sign = vec3Dot(ax, ay, az, bx, by, bz);
  if (sign > 1 - tolerance) {
    return Math.abs(planeA.dist - planeB.dist) < 0.1;
  } else if (sign < -1 + tolerance) {
    return Math.abs(planeA.dist + planeB.dist) < 0.1;
  } else {
    return false;
  }
}
